"use client";

import { ClerkProvider } from "@clerk/clerk-react";
import { config } from "@/config/env";
import { QueryProvider } from "./QueryProvider";
import { svSE, esES, enUS } from '@clerk/localizations';
import { getLanguageFromPath } from "@/lib/language";
import type { ReactNode } from "react";

interface AppProvidersProps {
  children: ReactNode;
}

interface DynamicClerkProviderProps {
  children: ReactNode;
  pathname: string;
}

/**
 * Dynamic Clerk Provider that updates localization based on URL
 */
export function DynamicClerkProvider({ children, pathname }: DynamicClerkProviderProps) {
  const currentLanguage = getLanguageFromPath(pathname);

  const clerkLocalization = (() => {
    switch (currentLanguage) {
      case 'sv':
        return svSE;
      case 'es':
        return esES;
      case 'en':
      default:
        return enUS;  
    }
  })();

  return (
    <ClerkProvider
      localization={clerkLocalization}
      publishableKey={config.clerkPublishableKey}
    >
      {children}
    </ClerkProvider>
  );
}

/**
 * Combined provider wrapper for the application.
 * Wraps children with: QueryProvider
 *
 * Note: BrowserRouter and ClerkProvider should be added at the App.tsx level
 * since they may need access to routing context.
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryProvider>{children}</QueryProvider>
  );
}
